import type {
  ConsumerValueSchema,
  HostChannelId,
  ParameterChannelId,
  ProjectedActionConsumer,
  ProjectedConsumerRead,
  ProjectedHostCapability,
  ProjectedResultChannel,
  ResultChannelId,
  RuntimeActionConsumerCatalogProjection,
} from "@cogo/schema/action-consumer-catalog"

import { ActionFlowEngine } from "./action-flow-engine"
import {
  sha256ActionConsumerText,
  type ActionConsumerAtomicOutcome,
  type ActionConsumerContract,
} from "./action-consumer-contract"
import { registerCfActions } from "./cf-actions"
import { registerFormulaActions } from "./formula-actions"
import { registerGoogleActions } from "./google-actions"
import { registerReportDesignAction } from "./report-design-action"
import { StateManager } from "./state-manager"
import { registerSupabaseActions } from "./supabase-actions"
import { registerWalletActions } from "./wallet-actions"

type BranchCondition = {
  param: string
  equals?: readonly (string | number | boolean | null)[]
  present?: boolean
}

type PlainRecord = Record<string, unknown>

const CONSUMER_READ_ROLES = new Set([
  "input",
  "selector",
  "payload",
  "target",
  "guard",
] as const)

const MISSING_CONSUMER_POLICIES = new Set([
  "reject-before-side-effects",
  "block-host",
] as const)

const SCALAR_VALUE_TYPES = new Set([
  "string",
  "number",
  "boolean",
  "null",
] as const)

const HOST_CAPABILITY_ID_PATTERN = /^host\.[A-Za-z0-9][A-Za-z0-9._-]*$/
const CHANNEL_SEGMENT_PATTERN = /^[^:]+$/

export function collectRuntimeActionConsumerContracts(): readonly ActionConsumerContract[] {
  const state = new StateManager(
    { state: {} },
    { localState: {}, inputSymbols: [], outputSymbols: [] },
    { permissive: true },
  )
  const engine = new ActionFlowEngine(state)
  const registrar = {
    registerBuiltin: engine.registerBuiltin.bind(engine),
    invokeRegisteredAction: engine.invokeRegisteredAction.bind(engine),
    state: engine.stateAccessor,
    compute: engine.computeAccessor,
    executeFlow: engine.executeFlow.bind(engine),
    findFlow: engine.findFlow.bind(engine),
  }
  registerFormulaActions(engine)
  registerWalletActions(engine, state, { baseUrl: "https://projection.invalid" })
  registerSupabaseActions(registrar, {} as never)
  registerCfActions(registrar)
  registerGoogleActions(registrar)
  registerReportDesignAction(engine, state)
  return engine.listActionConsumerContracts()
}

export function buildActionConsumerCatalogProjection(
  contracts: readonly ActionConsumerContract[],
): RuntimeActionConsumerCatalogProjection {
  const actions = contracts.map(projectContract).sort(compareAction)
  assertUniqueCanonicalValues(
    actions.map((action) => action.action),
    "actions",
  )
  assertUniqueChannelIds(actions)
  const withoutDigest = {
    schemaVersion: "cogo.action-consumer-catalog.v1" as const,
    source: "@cogo/runtime-v2" as const,
    actions,
  }
  return {
    schemaVersion: withoutDigest.schemaVersion,
    catalogDigest: `sha256:${hashStable(withoutDigest)}`,
    source: withoutDigest.source,
    actions,
  }
}

function projectContract(contract: ActionConsumerContract): ProjectedActionConsumer {
  const action = normalizeChannelSegment(contract.action, "action")
  const resultChannels: ProjectedResultChannel[] = []
  const hostCapabilities: ProjectedHostCapability[] = []
  const addAtomic = (outcome: ActionConsumerAtomicOutcome, when?: BranchCondition): void => {
    const condition = when ? normalizeCondition(when) : undefined
    const resultBody = projectResultBody(outcome, condition)
    resultChannels.push({
      channelId: deriveResultChannelId(action, resultBody),
      ...resultBody,
    })
    const hostBody = projectHostBody(resultBody)
    if (hostBody) {
      hostCapabilities.push({
        channelId: deriveHostChannelId(action, hostBody),
        ...hostBody,
      })
    }
  }
  if (contract.outcome.kind === "effect-set") {
    for (const branch of contract.outcome.branches) {
      for (const effect of branch.effects) addAtomic(effect, branch.when)
    }
  } else if (contract.outcome.kind !== "none") {
    addAtomic(contract.outcome)
  }
  return {
    action,
    runtimeAliases: normalizeUniqueStringArray(
      contract.runtimeAliases,
      "runtimeAliases",
    ).sort(compareText),
    reads: normalizeConsumerReads(contract.reads, action)
      .sort((left, right) => compareText(left.path, right.path) || compareText(left.role, right.role)),
    resultChannels: resultChannels.sort(compareStable),
    hostCapabilities: hostCapabilities.sort(compareStable),
    requiredHostCapabilities: normalizeHostCapabilityIds(
      contract.requiredHostCapabilities,
    ).sort(compareText),
    missingConsumerPolicy: normalizeMissingConsumerPolicy(
      contract.missingConsumerPolicy,
    ),
  }
}

function projectResultBody(
  outcome: ActionConsumerAtomicOutcome,
  condition?: BranchCondition,
): Omit<ProjectedResultChannel, "channelId"> {
  const candidate = requirePlainRecord(outcome, "outcome")
  const when = condition ? { when: condition } : {}
  if (candidate.kind === "state-target") {
    const hasTargetParam = hasOwn(candidate, "targetParam")
    const hasDefaultTarget = hasOwn(candidate, "defaultTarget")
    const hasDerivedTarget = hasOwn(candidate, "defaultTargetFromParam")
    if (!hasTargetParam && !hasDefaultTarget && !hasDerivedTarget) {
      throw new Error(
        "ACTION_CONSUMER_STATE_TARGET_INVALID:at least one target source",
      )
    }
    const targetParam = hasTargetParam
      ? {
          targetParam: requireNonEmptyString(
            candidate.targetParam,
            "outcome.targetParam",
          ),
        }
      : {}
    const defaultTarget = hasDefaultTarget
      ? {
          defaultTarget: requireNonEmptyString(
            candidate.defaultTarget,
            "outcome.defaultTarget",
          ),
        }
      : {}
    const defaultTargetFromParam = hasDerivedTarget
      ? {
          defaultTargetFromParam: normalizeDefaultTargetFromParam(
            candidate.defaultTargetFromParam,
          ),
        }
      : {}
    if (hasDefaultTarget && hasDerivedTarget) {
      throw new Error(
        "ACTION_CONSUMER_STATE_TARGET_INVALID:defaultTarget conflicts with defaultTargetFromParam",
      )
    }
    return {
      kind: candidate.kind,
      ...when,
      ...targetParam,
      ...defaultTarget,
      ...defaultTargetFromParam,
      ...(candidate.targetSuffixes === undefined
        ? {}
        : {
            targetSuffixes: normalizeUniqueStringArray(
              candidate.targetSuffixes,
              "outcome.targetSuffixes",
            ).sort(compareText),
          }),
      ...(candidate.envelope === undefined
        ? {}
        : { envelope: normalizeValueSchema(candidate.envelope) }),
    }
  }
  if (candidate.kind === "return-capture") {
    if (candidate.capture !== "step-name") {
      throw new Error("ACTION_CONSUMER_OUTCOME_INVALID:return-capture.capture")
    }
    return {
      kind: candidate.kind,
      ...when,
      capture: candidate.capture,
      ...(candidate.envelope === undefined
        ? {}
        : { envelope: normalizeValueSchema(candidate.envelope) }),
    }
  }
  if (candidate.kind === "host-effect") {
    return {
      kind: candidate.kind,
      ...when,
      capabilityId: normalizeHostCapabilityId(
        candidate.capability,
        "outcome.capability",
      ),
      operation: requireNonEmptyString(
        candidate.operation,
        "outcome.operation",
      ),
    }
  }
  if (candidate.kind === "host-registry") {
    return {
      kind: candidate.kind,
      ...when,
      capabilityId: normalizeHostCapabilityId(
        candidate.capability,
        "outcome.capability",
      ),
      selectorParam: requireNonEmptyString(
        candidate.selectorParam,
        "outcome.selectorParam",
      ),
      ...optionalStringField(
        candidate,
        "payloadParam",
        "outcome.payloadParam",
      ),
    }
  }
  throw new Error(`ACTION_CONSUMER_OUTCOME_INVALID:${String(candidate.kind)}`)
}

function projectHostBody(
  outcome: Omit<ProjectedResultChannel, "channelId">,
): Omit<ProjectedHostCapability, "channelId"> | undefined {
  if (outcome.kind !== "host-effect" && outcome.kind !== "host-registry") return undefined
  return {
    capabilityId: normalizeHostCapabilityId(
      outcome.capabilityId,
      "resultChannel.capabilityId",
    ),
    operation: outcome.kind === "host-effect"
      ? requireNonEmptyString(outcome.operation, "resultChannel.operation")
      : "invoke",
    ...(outcome.when ? { when: outcome.when } : {}),
  }
}

function deriveParameterChannelId(action: string, path: string): ParameterChannelId {
  return `parameter-channel:${action}:${path}`
}

function deriveResultChannelId(
  action: string,
  body: Omit<ProjectedResultChannel, "channelId">,
): ResultChannelId {
  return `result-channel:${action}:sha256:${hashStable(body)}`
}

function deriveHostChannelId(
  action: string,
  body: Omit<ProjectedHostCapability, "channelId">,
): HostChannelId {
  return `host-channel:${action}:sha256:${hashStable(body)}`
}

function assertUniqueChannelIds(actions: readonly ProjectedActionConsumer[]): void {
  const channelIds = new Set<string>()
  for (const action of actions) {
    for (const row of [...action.reads, ...action.resultChannels, ...action.hostCapabilities]) {
      if (channelIds.has(row.channelId)) {
        throw new Error(`ACTION_CONSUMER_CHANNEL_ID_COLLISION:${row.channelId}`)
      }
      channelIds.add(row.channelId)
    }
  }
}

function normalizeCondition(condition: unknown): BranchCondition {
  const candidate = requirePlainRecord(condition, "condition")
  const hasEquals = hasOwn(candidate, "equals")
  const hasPresent = hasOwn(candidate, "present")
  if (hasEquals === hasPresent) {
    throw new Error("ACTION_CONSUMER_CONDITION_INVALID:exactly-one-operator")
  }
  const param = requireNonEmptyString(candidate.param, "condition.param")
  if (hasEquals) {
    if (!Array.isArray(candidate.equals) || candidate.equals.length === 0) {
      throw new Error("ACTION_CONSUMER_CONDITION_INVALID:condition.equals")
    }
    return {
      param,
      equals: assertUniqueCanonicalValues(
        candidate.equals.map((value, index) =>
          normalizeConditionPrimitive(value, index),
        ),
        "condition.equals",
      ).sort(comparePrimitive),
    }
  }
  if (typeof candidate.present !== "boolean") {
    throw new Error("ACTION_CONSUMER_CONDITION_INVALID:condition.present")
  }
  return { param, present: candidate.present }
}

function normalizeConditionPrimitive(
  value: unknown,
  index: number,
): string | number | boolean | null {
  if (value === null || typeof value === "string" || typeof value === "boolean") {
    return value
  }
  if (typeof value === "number" && Number.isFinite(value)) return value
  throw new Error(`ACTION_CONSUMER_CANONICAL_VALUE_INVALID:condition.equals[${index}]`)
}

function normalizeValueSchema(
  schema: unknown,
  seen = new WeakSet<object>(),
): ConsumerValueSchema {
  let candidate: PlainRecord
  try {
    candidate = requirePlainRecord(schema, "valueSchema")
  } catch {
    throw new Error("ACTION_CONSUMER_VALUE_SCHEMA_INVALID:valueSchema")
  }
  if (seen.has(candidate)) {
    throw new Error("ACTION_CONSUMER_VALUE_SCHEMA_INVALID:circular")
  }
  seen.add(candidate)
  try {
    if (candidate.kind === "unknown") return { kind: "unknown" }
    if (candidate.kind === "scalar") {
      if (!SCALAR_VALUE_TYPES.has(candidate.type as never)) {
        throw new Error("ACTION_CONSUMER_VALUE_SCHEMA_INVALID:scalar.type")
      }
      return {
        kind: "scalar",
        type: candidate.type as "string" | "number" | "boolean" | "null",
      }
    }
    if (candidate.kind === "array") {
      return { kind: "array", items: normalizeValueSchema(candidate.items, seen) }
    }
    if (candidate.kind === "object") {
      const properties = requirePlainRecord(
        candidate.properties,
        "valueSchema.properties",
      )
      return {
        kind: "object",
        properties: Object.fromEntries(
          Object.keys(properties)
            .sort(compareText)
            .map((key) => [key, normalizeValueSchema(properties[key], seen)]),
        ),
        required: normalizeUniqueStringArray(
          candidate.required,
          "valueSchema.required",
        ).sort(compareText),
      }
    }
  } finally {
    seen.delete(candidate)
  }
  throw new Error(`ACTION_CONSUMER_VALUE_SCHEMA_INVALID:${String(candidate.kind)}`)
}

function normalizeConsumerReads(
  reads: unknown,
  action: string,
): ProjectedConsumerRead[] {
  if (!Array.isArray(reads)) {
    throw new Error("ACTION_CONSUMER_CANONICAL_VALUE_INVALID:reads")
  }
  return reads.map((read, index) => {
    const candidate = requirePlainRecord(read, `reads[${index}]`)
    const path = normalizeChannelSegment(candidate.path, `reads[${index}].path`)
    if (!CONSUMER_READ_ROLES.has(candidate.role as never)) {
      throw new Error(`ACTION_CONSUMER_READ_INVALID:reads[${index}].role`)
    }
    if (typeof candidate.required !== "boolean") {
      throw new Error(`ACTION_CONSUMER_READ_INVALID:reads[${index}].required`)
    }
    return {
      ...(candidate.aliases === undefined
        ? {}
        : {
            aliases: normalizeUniqueStringArray(
              candidate.aliases,
              `reads[${index}].aliases`,
            ).sort(compareText),
          }),
      channelId: deriveParameterChannelId(action, path),
      path,
      required: candidate.required,
      role: candidate.role as ProjectedConsumerRead["role"],
    }
  })
}

function normalizeChannelSegment(value: unknown, label: string): string {
  if (typeof value !== "string" || !CHANNEL_SEGMENT_PATTERN.test(value)) {
    throw new Error(`ACTION_CONSUMER_CHANNEL_SEGMENT_INVALID:${label}`)
  }
  return value
}

function normalizeStringArray(value: unknown, label: string): string[] {
  if (!Array.isArray(value)) {
    throw new Error(`ACTION_CONSUMER_CANONICAL_VALUE_INVALID:${label}`)
  }
  return value.map((entry, index) =>
    requireNonEmptyString(entry, `${label}[${index}]`),
  )
}

function normalizeUniqueStringArray(value: unknown, label: string): string[] {
  return assertUniqueCanonicalValues(normalizeStringArray(value, label), label)
}

function assertUniqueCanonicalValues<T>(
  values: readonly T[],
  label: string,
): T[] {
  const identities = new Set<string>()
  for (const value of values) {
    const identity = stableJson(value)
    if (identities.has(identity)) {
      throw new Error(
        `ACTION_CONSUMER_DUPLICATE_VALUE:${label}:${identity}`,
      )
    }
    identities.add(identity)
  }
  return [...values]
}

function normalizeHostCapabilityIds(value: unknown): `host.${string}`[] {
  if (!Array.isArray(value)) {
    throw new Error(
      "ACTION_CONSUMER_CANONICAL_VALUE_INVALID:requiredHostCapabilities",
    )
  }
  return assertUniqueCanonicalValues(
    value.map((entry, index) =>
      normalizeHostCapabilityId(entry, `requiredHostCapabilities[${index}]`),
    ),
    "requiredHostCapabilities",
  )
}

function normalizeHostCapabilityId(
  value: unknown,
  label: string,
): `host.${string}` {
  if (typeof value !== "string" || !HOST_CAPABILITY_ID_PATTERN.test(value)) {
    throw new Error(`ACTION_CONSUMER_HOST_CAPABILITY_INVALID:${label}`)
  }
  return value as `host.${string}`
}

function normalizeMissingConsumerPolicy(
  value: unknown,
): ProjectedActionConsumer["missingConsumerPolicy"] {
  if (!MISSING_CONSUMER_POLICIES.has(value as never)) {
    throw new Error("ACTION_CONSUMER_MISSING_POLICY_INVALID")
  }
  return value as ProjectedActionConsumer["missingConsumerPolicy"]
}

function normalizeDefaultTargetFromParam(
  value: unknown,
): NonNullable<ProjectedResultChannel["defaultTargetFromParam"]> {
  const candidate = requirePlainRecord(value, "outcome.defaultTargetFromParam")
  return {
    prefix: requireNonEmptyString(
      candidate.prefix,
      "outcome.defaultTargetFromParam.prefix",
    ),
    param: requireNonEmptyString(
      candidate.param,
      "outcome.defaultTargetFromParam.param",
    ),
    ...optionalStringField(
      candidate,
      "fallback",
      "outcome.defaultTargetFromParam.fallback",
    ),
  }
}

function optionalStringField<K extends string>(
  value: PlainRecord,
  key: K,
  label: string,
): Partial<Record<K, string>> {
  if (value[key] === undefined) return {}
  return { [key]: requireNonEmptyString(value[key], label) } as Record<K, string>
}

function requireNonEmptyString(value: unknown, label: string): string {
  if (typeof value !== "string" || value.length === 0) {
    throw new Error(`ACTION_CONSUMER_CANONICAL_VALUE_INVALID:${label}`)
  }
  return value
}

function requirePlainRecord(value: unknown, label: string): PlainRecord {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`ACTION_CONSUMER_CANONICAL_VALUE_INVALID:${label}`)
  }
  const prototype = Object.getPrototypeOf(value)
  if (prototype !== Object.prototype && prototype !== null) {
    throw new Error(`ACTION_CONSUMER_CANONICAL_VALUE_INVALID:${label}`)
  }
  return value as PlainRecord
}

function hasOwn(value: PlainRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key)
}

export function renderActionConsumerCatalogJson(
  projection: RuntimeActionConsumerCatalogProjection,
): string {
  return `${JSON.stringify(projection, null, 2)}\n`
}

export function renderActionConsumerContractsTtl(
  projection: RuntimeActionConsumerCatalogProjection,
): string {
  const lines = [
    "# Generated by @cogo/runtime-v2. Do not edit.",
    "@prefix rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#> .",
    "@prefix rdfs: <http://www.w3.org/2000/01/rdf-schema#> .",
    "@prefix cogo: <https://cogo.xyz/ns/v1#> .",
    "",
    "cogo:ActionConsumerContract a rdfs:Class .",
    "cogo:actionConsumerCatalogV1 a cogo:ActionConsumerCatalog ;",
    `    cogo:catalogDigest ${ttlString(projection.catalogDigest)} .`,
    "",
  ]
  for (const action of projection.actions) {
    const details = [
      ...action.runtimeAliases.map((value) => ["runtimeAlias", value] as const),
      ...action.reads.map((value) => ["consumerRead", stableJson(value)] as const),
      ...action.resultChannels.map((value) => ["resultChannel", stableJson(value)] as const),
      ...action.hostCapabilities.map((value) => ["hostCapability", stableJson(value)] as const),
      ...action.requiredHostCapabilities.map((value) => ["requiredHostCapability", value] as const),
    ]
    lines.push(
      `<https://cogo.xyz/instances/action-consumer/${encodeURIComponent(action.action)}> a cogo:ActionConsumerContract ;`,
      `    cogo:canonicalAction ${ttlString(action.action)} ;`,
      `    cogo:missingConsumerPolicy ${ttlString(action.missingConsumerPolicy)}${details.length ? " ;" : " ."}`,
    )
    details.forEach(([predicate, value], index) => {
      lines.push(`    cogo:${predicate} ${ttlString(value)}${index === details.length - 1 ? " ." : " ;"}`)
    })
    lines.push("")
  }
  return lines.join("\n")
}

function hashStable(value: unknown): string {
  return sha256ActionConsumerText(stableJson(value))
}

function stableJson(value: unknown): string {
  if (value === null) return "null"
  if (typeof value === "string" || typeof value === "boolean") {
    return JSON.stringify(value)
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error("ACTION_CONSUMER_CANONICAL_VALUE_INVALID:number")
    }
    return JSON.stringify(value)
  }
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`
  if (typeof value === "object") {
    const candidate = requirePlainRecord(value, "stableJson")
    return `{${Object.entries(candidate)
      .sort(([left], [right]) => compareText(left, right))
      .map(([key, entry]) => `${JSON.stringify(key)}:${stableJson(entry)}`)
      .join(",")}}`
  }
  throw new Error(`ACTION_CONSUMER_CANONICAL_VALUE_INVALID:${typeof value}`)
}

function compareAction(left: ProjectedActionConsumer, right: ProjectedActionConsumer): number {
  return compareText(left.action, right.action)
}
function compareStable(left: unknown, right: unknown): number {
  return compareText(stableJson(left), stableJson(right))
}
function compareText(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0
}
function comparePrimitive(
  left: string | number | boolean | null,
  right: string | number | boolean | null,
): number {
  return compareText(stableJson(left), stableJson(right))
}
function ttlString(value: string): string {
  return JSON.stringify(value)
}
